import React, { useState, useEffect, useRef } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { usePersistedState } from '../../hooks/usePersistedState'
import { useHospitalData } from '../../hooks/useHospitalData'
import LineChart, { LineChartHandle } from '../../components/Charts/LineChart'
import CustomSelect from '../../components/Common/CustomSelect'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import { sortTimePeriods, trimTrailingEmptyPeriods, filterFutureMonths, createChartExportHandlers, createMenuToggle, setupMenuClickOutside } from '../../utils/exportUtils'
import { generatePDFIds } from '../../utils/pdfIdHelper'
import './ProviderEducation.css'

const ProviderEducation: React.FC = () => {
    const { selectedHospitalIds, isMultiSelect } = useHospitalSelection()

    const { data, loading, initialLoading } = useHospitalData(
        '/api/pmh/process/provider_education',
        selectedHospitalIds
    )

    const [interval, setInterval] = usePersistedState('pmh_provider_edu_interval', 'monthly')
    const [chartData, setChartData] = useState<{ categories: string[], series: any[] }>({ categories: [], series: [] })
    const [noDataMessage, setNoDataMessage] = useState<string | undefined>(undefined)

    const chartRef = useRef<LineChartHandle>(null)
    const chartContainerRef = useRef<HTMLDivElement>(null)

    const menuId = 'export-menu-provider'
    const toggleMenu = createMenuToggle(menuId)
    const { handleDownloadPNG, handleDownloadSVG, closeMenu } = createChartExportHandlers(
        chartContainerRef,
        'Clinical Team Education',
        'Clinical_Team_Education',
        { fontBoost: true, exportTitle: 'Clinical Team Education' }
    )

    useEffect(() => {
        return setupMenuClickOutside(() => closeMenu(menuId));
    }, []);

    const safeParseEducationPct = (value: any): number | null => {
        if (value === null || value === undefined || value === '' || value === '""') {
            return null
        }

        if (value === 0 || value === "0") {
            return 0
        }

        const num = parseFloat(value)
        return isNaN(num) ? null : num
    }

    useEffect(() => {
        if (!data) {
            setChartData({ categories: [], series: [] })
            setNoDataMessage("No Data Available")
            return
        }

        const rows = data?.response?.result?.rows || []
        const columns = data?.response?.result?.column_order || []

        if (rows.length === 0) {
            setChartData({ categories: [], series: [] })
            setNoDataMessage("No Data Available")
            return
        }

        const intervalMap: Record<string, string> = {
            'monthly': 'Monthly',
            'quarterly': 'Quarterly',
            'bi-yearly': 'Bi-Yearly',
            'yearly': 'Yearly'
        }

        const filteredRows = rows.filter((row: any[]) => {
            return row[2] === intervalMap[interval]
        })

        if (filteredRows.length === 0) {
            setChartData({ categories: [], series: [] })
            setNoDataMessage("No Data Available")
            return
        }

        const periodData: Record<string, Record<string, (number | null)[]>> = {}

        const seriesKeys = [
            { name: 'OB Providers', col: 'percent_ob_education' },
            { name: 'OB Nurses', col: 'percent_ob_nurse_education' },
            { name: 'ER Providers', col: 'percent_er_education' },
            { name: 'ER Nurses', col: 'percent_er_nurse_education' }
        ]

        filteredRows.forEach((row: any[]) => {
            const timePeriod = String(row[1])

            if (!periodData[timePeriod]) {
                periodData[timePeriod] = {}
                seriesKeys.forEach(k => periodData[timePeriod][k.name] = [])
            }

            seriesKeys.forEach(k => {
                const colIndex = columns.indexOf(k.col)
                const rawValue = row[colIndex]
                const val = safeParseEducationPct(rawValue)
                periodData[timePeriod][k.name].push(val)
            })
        })

        const sortedPeriods = filterFutureMonths(sortTimePeriods(Object.keys(periodData), interval))
        const series: any[] = []

        seriesKeys.forEach(k => {
            const data = sortedPeriods.map(period => {
                const vals = periodData[period][k.name]
                if (!vals || vals.length === 0) return null

                const validValues = vals.filter(v => v !== null) as number[]
                if (validValues.length === 0) return null

                const avg = validValues.reduce((a, b) => a + b, 0) / validValues.length
                return Math.round(avg * 10) / 10
            })
            series.push({ name: k.name, data })
        })

        const hasValidData = series.some(s => s.data.some((v: any) => v !== null))

        if (!hasValidData) {
            const emptySeries = series.map(s => ({ ...s, data: sortedPeriods.map(() => null) }))
            setChartData({ categories: sortedPeriods, series: emptySeries })
            setNoDataMessage("No Data Available")
            return
        }

        setNoDataMessage(undefined)
        const trimmed = trimTrailingEmptyPeriods(sortedPeriods, series)
        setChartData({
            categories: trimmed.categories,
            series: trimmed.series
        })

    }, [data, interval, isMultiSelect])

    if (initialLoading) return <LoadingNotice title="Loading clinical team education data..." />

    return (
        <div id={generatePDFIds.dashboard()} className="provider-education-page">
            <div className="page-header">
                <h1 className="page-title">Process Measures</h1>
                <div className="header-controls">
                    <CustomSelect
                        value={interval}
                        onChange={setInterval}
                        options={[
                            { value: 'monthly', label: 'Monthly' },
                            { value: 'quarterly', label: 'Quarterly' },
                            { value: 'bi-yearly', label: 'Bi-Yearly' },
                            { value: 'yearly', label: 'Yearly' }
                        ]}
                    />
                </div>
            </div>

            <div id={generatePDFIds.chart(1)} className="chart-section" ref={chartContainerRef}>
                <div className="chart-title-wrapper">
                    <h2 className="section-title">Clinical Team Education</h2>
                    <div className="menu-container export-menu-container" style={{ position: 'relative' }}>
                        <button className="hamburger" onClick={toggleMenu}>&#9776;</button>
                        <div id={menuId} className="export-dropdown">
                            <div onClick={() => { handleDownloadSVG(menuId); }}>Download SVG</div>
                            <div onClick={() => { handleDownloadPNG(menuId); }}>Download PNG</div>
                            <div onClick={() => { chartRef.current?.exportToCSV(); closeMenu(menuId); }}>Download CSV</div>
                        </div>
                    </div>
                </div>
                <p className="chart-description">
                    Percent of clinicians caring for pregnant/postpartum patients with PMH training completed
                </p>
                <LineChart
                    ref={chartRef}
                    categories={chartData.categories}
                    series={chartData.series}
                    height={450}
                    goalLine={80}
                    goalLabel="Goal ≥ 80%"
                    min={0}
                    max={100}
                    tickAmount={20}
                    yAxisLabel="Clinical Team Education %"
                    xAxisLabel="Time Period"
                    colors={["#0072B2", "#D55E00", "#F0E441", "#009E73"]}
                    legendPosition="top"
                    legendAlign="center"
                    noDataMessage={noDataMessage}
                    showXAxisTooltip={true}
                    exportFilename="Clinical_Team_Education"
                    showToolbar={false}
                    isLoading={loading}
                    decimals={1}
                />
                <p className="data-source">Data from hospital level data form</p>
            </div>
        </div>
    )
}

export default ProviderEducation